import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FIELD_LABELS, SECTION_LABELS, STAGE_LABELS } from "@/components/insights/Panels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const time = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function describe(e) {
  const p = e.props || {};
  switch (`${e.type}:${e.name}`) {
    case "pageview:view": return `Viewed ${p.path || "page"}`;
    case "stage:define": case "stage:analysing": case "stage:results": case "stage:campaign": return `Stage: ${STAGE_LABELS[e.name]}`;
    case "audience:area_added": return `Added postcode ${p.code}`;
    case "audience:area_removed": return `Removed postcode ${p.code}`;
    case "audience:filters_changed": return `Audience set to "${p.preset}"`;
    case "audience:audience_built": return `Built audience: ${(p.total || 0).toLocaleString("en-GB")} households across ${(p.areas || []).join(", ")} ("${p.preset}")`;
    case "audience:format_selected": return `Chose format: ${p.format}`;
    case "audience:free_sample": return p.on ? "Ticked free sample pack" : "Unticked free sample pack";
    case "form:field_focus": return `Clicked into ${FIELD_LABELS[p.field] || p.field}`;
    case "form:submit_attempt": return p.errors?.length ? `Tried to submit — errors: ${p.errors.map((f) => FIELD_LABELS[f] || f).join(", ")}` : "Submitted the form";
    case "form:submit_ok": return `Enquiry sent (${p.ref})`;
    case "form:submit_fail": return "Enquiry failed to send";
    case "scroll:25": case "scroll:50": case "scroll:75": case "scroll:100": return `Scrolled ${e.name}% of the page`;
    case "click:": return "Click";
    default:
      if (e.type === "funnel") return `Milestone: ${e.name.replace(/_/g, " ")}`;
      if (e.type === "click") return `Clicked ${e.name.replace(/[-_]/g, " ")}`;
      if (e.type === "section_time") return `Spent ${Math.round((p.ms || 0) / 1000)}s on ${SECTION_LABELS[e.name] || STAGE_LABELS[e.name.replace("stage:", "")] || e.name}`;
      return `${e.type} ${e.name}`;
  }
}

export default function JourneyDialog({ sessionId, token, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!sessionId) return;
    setData(null);
    axios.get(`${API}/analytics/sessions/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [sessionId, token]);
  const s = data?.summary;
  return (
    <Dialog open={!!sessionId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0C2010] text-white" data-testid="journey-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold tracking-[-0.03em]">Visitor journey</DialogTitle></DialogHeader>
        {!data && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#C6EE5A]" /></div>}
        {data?.error && <p className="py-10 text-center text-white/50">Couldn't load this journey.</p>}
        {s && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 text-[13px] sm:grid-cols-4">
              {[["Where", [s.city, s.region].filter(Boolean).join(", ") || s.country || "Unknown"], ["Device", s.device], ["Source", s.referrer], ["Active time", `${s.activeSec}s`], ["Visitor", s.returning ? "Returning" : "New"], ["Scrolled", `${data.session.maxScroll || 0}%`], ["Furthest", s.furthest], ["Events", s.events]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-3"><p className="eyebrow-muted">{k}</p><p className="mt-1 capitalize text-white">{v}</p></div>
              ))}
            </div>
            {data.enquiry && (
              <div className="rounded-2xl border border-[#96C11F]/40 bg-[#96C11F]/10 p-4 text-[13px]" data-testid="journey-enquiry">
                <p className="eyebrow">Enquiry submitted — {data.enquiry.reference}</p>
                <p className="mt-2 text-white/85">{data.enquiry.customer?.firstName} {data.enquiry.customer?.lastName}, {data.enquiry.customer?.agency}</p>
                <p className="mt-1 text-white/70">{(data.enquiry.selectedQuantity || 0).toLocaleString("en-GB")} households · {(data.enquiry.postcodeDistricts || []).join(", ")} · {gbp(data.enquiry.estimatedPrice)}{data.enquiry.customer?.freeSample ? " · free sample requested" : ""}</p>
              </div>
            )}
            <ol className="relative space-y-3 border-l border-white/15 pl-5" data-testid="journey-timeline">
              {data.timeline.map((e, i) => (
                <li key={i} className="relative text-[13px]">
                  <span className="absolute -left-[26px] top-1.5 h-2 w-2 rounded-full bg-[#96C11F]" />
                  <span className="mr-3 font-ui text-[11px] text-white/35">{time(e.ts)}</span><span className="text-white/85">{describe(e)}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
